import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://spectraine-backend.onrender.com';

const C = {
  bg: '#080d18',
  nav: '#0a1020',
  surface: '#0f172a',
  surface2: '#111d35',
  border: '#1e3a5f',
  teal: '#22d3ee',
  tealDark: '#06b6d4',
  blue: '#6366f1',
  blueDark: '#4f46e5',
  text: '#e2e8f0',
  muted: '#64748b',
  faint: '#1e293b',
  critical: '#ef4444',
  low: '#10b981',
  white: '#ffffff',
};

const GRAD = `linear-gradient(135deg, ${C.teal} 0%, ${C.blue} 100%)`;

const gradBtn = (extra = {}) => ({
  background: GRAD,
  color: C.white,
  border: 'none',
  borderRadius: 10,
  padding: '12px 28px',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 15,
  fontFamily: 'Inter, sans-serif',
  letterSpacing: '0.01em',
  boxShadow: `0 0 24px rgba(34,211,238,0.2)`,
  transition: 'all 0.2s',
  width: '100%',
  ...extra,
});

const inp = {
  background: '#0a1220',
  border: `1px solid ${C.border}`,
  borderRadius: 9,
  padding: '11px 14px',
  color: C.text,
  fontSize: 14,
  width: '100%',
  boxSizing: 'border-box',
  marginBottom: 14,
  fontFamily: 'Inter, sans-serif',
  outline: 'none',
  transition: 'border-color 0.2s',
};

const fieldLabel = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: C.muted,
  marginBottom: 5,
  letterSpacing: '0.04em',
};

const LoginPage = ({ onSwitchToRegister }) => {
  const [view, setView] = useState('login'); // login | forgot | reset_sent
  const [form, setForm] = useState({ email: '', password: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('email', form.email);
      fd.append('password', form.password);
      const res = await fetch(`${BACKEND_URL}/login`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('email', forgotEmail);
      await fetch(`${BACKEND_URL}/forgot-password`, { method: 'POST', body: fd });
      setView('reset_sent');
    } catch (err) {
      setError('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const pageWrap = {
    minHeight: '100vh',
    background: C.bg,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 20px',
    fontFamily: 'Inter, -apple-system, sans-serif',
    position: 'relative',
    overflow: 'hidden',
  };

  const glowStyle = {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(80px)',
    pointerEvents: 'none',
  };

  const cardStyle = {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 20,
    padding: '36px 40px',
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 8px 48px rgba(0,0,0,0.5)',
    position: 'relative',
    zIndex: 1,
  };

  // ── Reset sent ──────────────────────────────────────────────────────────
  if (view === 'reset_sent') {
    return (
      <div style={pageWrap}>
        <div style={{ ...glowStyle, width: 400, height: 400, top: -100, left: -100, background: `${C.teal}08` }} />
        <div style={{ ...glowStyle, width: 300, height: 300, bottom: -80, right: -80, background: `${C.blue}08` }} />
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: `${C.low}18`, border: `1px solid ${C.low}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 24 }}>✓</div>
            <h2 style={{ margin: '0 0 8px', color: C.white, fontSize: 22, fontWeight: 800 }}>Check your inbox</h2>
            <p style={{ margin: 0, color: C.muted, fontSize: 14, lineHeight: 1.7 }}>
              If that email exists, a password reset link has been sent. Check your inbox and spam folder.
            </p>
          </div>
          <button onClick={() => setView('login')} style={gradBtn()}>Back to Login</button>
        </div>
      </div>
    );
  }

  // ── Forgot password ─────────────────────────────────────────────────────
  if (view === 'forgot') {
    return (
      <div style={pageWrap}>
        <div style={{ ...glowStyle, width: 400, height: 400, top: -100, left: -100, background: `${C.teal}08` }} />
        <div style={{ ...glowStyle, width: 300, height: 300, bottom: -80, right: -80, background: `${C.blue}08` }} />

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 32, position: 'relative', zIndex: 1 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z" stroke={C.teal} strokeWidth="2" strokeLinejoin="round" />
            <path d="M9 12l2 2 4-4" stroke={C.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontWeight: 800, fontSize: 17, letterSpacing: '0.06em', background: GRAD, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>SPECTRAINE</span>
        </div>

        <div style={cardStyle}>
          <h2 style={{ margin: '0 0 6px', color: C.white, fontSize: 22, fontWeight: 800 }}>Reset Password</h2>
          <p style={{ margin: '0 0 24px', color: C.muted, fontSize: 14 }}>Enter your email and we'll send you a reset link.</p>

          {error && (
            <div style={{ background: `${C.critical}15`, border: `1px solid ${C.critical}40`, borderRadius: 8, padding: '10px 14px', color: C.critical, fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleForgot}>
            <label style={fieldLabel}>EMAIL ADDRESS</label>
            <input style={inp} type="email" placeholder="you@company.com" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required />
            <button type="submit" style={gradBtn({ marginTop: 4 })} disabled={loading}>
              {loading ? 'Sending…' : 'Send Reset Link'}
            </button>
          </form>

          <p style={{ textAlign: 'center', margin: '20px 0 0', fontSize: 13, color: C.muted }}>
            <button onClick={() => setView('login')} style={{ background: 'none', border: 'none', color: C.teal, cursor: 'pointer', fontSize: 13, fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
              ← Back to login
            </button>
          </p>
        </div>
      </div>
    );
  }

  // ── Login ───────────────────────────────────────────────────────────────
  return (
    <div style={pageWrap}>
      {/* Ambient glows */}
      <div style={{ ...glowStyle, width: 500, height: 500, top: -150, left: -150, background: `${C.teal}06` }} />
      <div style={{ ...glowStyle, width: 400, height: 400, bottom: -100, right: -100, background: `${C.blue}07` }} />

      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 32, position: 'relative', zIndex: 1 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z" stroke={C.teal} strokeWidth="2" strokeLinejoin="round" />
          <path d="M9 12l2 2 4-4" stroke={C.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span style={{ fontWeight: 800, fontSize: 17, letterSpacing: '0.06em', background: GRAD, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>SPECTRAINE</span>
      </div>

      <div style={cardStyle}>
        <h2 style={{ margin: '0 0 6px', color: C.white, fontSize: 22, fontWeight: 800 }}>Welcome back</h2>
        <p style={{ margin: '0 0 28px', color: C.muted, fontSize: 14 }}>Log in to your Spectraine account</p>

        {error && (
          <div style={{ background: `${C.critical}15`, border: `1px solid ${C.critical}40`, borderRadius: 8, padding: '10px 14px', color: C.critical, fontSize: 13, marginBottom: 18 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <label style={fieldLabel}>EMAIL ADDRESS</label>
          <input
            style={inp}
            type="email"
            placeholder="you@company.com"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            required
          />
          <label style={fieldLabel}>PASSWORD</label>
          <input
            style={{ ...inp, marginBottom: 6 }}
            type="password"
            placeholder="••••••••"
            value={form.password}
            onChange={e => setForm({ ...form, password: e.target.value })}
            required
          />

          <div style={{ textAlign: 'right', marginBottom: 22 }}>
            <button
              type="button"
              onClick={() => setView('forgot')}
              style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12, fontFamily: 'Inter, sans-serif', padding: 0 }}
            >
              Forgot password?
            </button>
          </div>

          <button type="submit" style={gradBtn()} disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' }}>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <span style={{ color: C.muted, fontSize: 12, fontWeight: 600 }}>OR</span>
          <div style={{ flex: 1, height: 1, background: C.border }} />
        </div>

        <p style={{ textAlign: 'center', margin: 0, fontSize: 14, color: C.muted }}>
          Don't have an account?{' '}
          {onSwitchToRegister ? (
            <button onClick={onSwitchToRegister} style={{ background: 'none', border: 'none', color: C.teal, cursor: 'pointer', fontSize: 14, fontFamily: 'Inter, sans-serif', fontWeight: 700, padding: 0 }}>
              Sign up free
            </button>
          ) : (
            <Link to="/register" style={{ color: C.teal, textDecoration: 'none', fontWeight: 700 }}>Sign up free</Link>
          )}
        </p>
      </div>

      {/* Trust badges */}
      <div style={{ display: 'flex', gap: 20, marginTop: 28, position: 'relative', zIndex: 1 }}>
        {['🔒 SOC 2 Ready', '⚡ 2-min setup', '✓ No card stored'].map(t => (
          <span key={t} style={{ fontSize: 12, color: C.muted, display: 'flex', alignItems: 'center', gap: 4 }}>{t}</span>
        ))}
      </div>
    </div>
  );
};

export default LoginPage;
